import axios from 'axios';
import { apiClient, API_CONFIG } from '../httpClient';
import { getIpAndRegion } from '../ipRegion';
import { handleLoginError } from '../utils/errorHandler';
import tokenService from '../../utils/tokenService';
import { LOCAL_STORAGE_KEYS } from '../../config/appConstants';


export const authService = {
  // 로그인
  login: async (username, password) => {
    try {
      const { ip, region } = await getIpAndRegion();
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);
      formData.append('grant_type', 'password');
      formData.append('client_secret', API_CONFIG.CLIENT_SECRET);
      formData.append('ip_address', ip);
      formData.append('region', region);

      const response = await apiClient.post('/checkin', formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (response.status === 200 && response.data?.access_token) {
        await tokenService.setToken(response.data.access_token);
        if (response.data.expire_time_at_utc) {
          tokenService.setTokenExpiry(response.data.expire_time_at_utc);
        }
        return { success: true, data: response.data };
      }

      return {
        success: false,
        error: {
          code: response.status,
          message: 'unknown'
        }
      };

    } catch (error) {
      return handleLoginError(error);
    }
  },

  // 회원가입
  signUp: async (userData) => {
    try {
      const { ip } = await getIpAndRegion();

      const requestData = {
        user_id: userData.userId.toLowerCase(),
        password: userData.password,
        name: userData.fullName,
        department: userData.department.toLowerCase(),
        region: userData.region.toLowerCase(),
        client_secret: API_CONFIG.CLIENT_SECRET,
        registration_ip: ip,
        initial_str: userData.initialStr || null
      };

      const response = await apiClient.post('/user/v1/first', requestData);

      if (response.status === 200 || response.status === 201) {
        return { success: true, data: response.data };
      }

      return {
        success: false,
        error: {
          code: response.status,
          message: 'signup_failed'
        }
      };

    } catch (error) {
      const detail = error?.response?.data?.detail || '';
      if (/user.?id.*already exists/i.test(detail)) {
        return { success: false, error: { code: 400, message: 'userIdExists', detail } };
      }
      if (/initial.?string.*already exists/i.test(detail)) {
        return { success: false, error: { code: 400, message: 'initialStrExists', detail } };
      }
      if (/password/i.test(detail)) {
        return { success: false, error: { code: 400, message: 'passwordInvalid', detail } };
      }
      return handleLoginError(error);
    }
  },

  // 사용자 정보 조회
  getCurrentUser: async () => {
    try {
      const response = await apiClient.get('/user/v1/me');
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: {
          code: error.response?.status || 500,
          message: 'user_fetch_failed'
        }
      };
    }
  },

  // 사용자 정보 수정
  updateUser: async (userData) => {
    try {
      const requestData = {
        user_id: userData.userId,
        name: userData.name,
        department: userData.department,
        region: userData.region
      };
      const response = await apiClient.put('/user/v1/modify', requestData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: {
          code: error.response?.status || 500,
          message: 'user_update_failed'
        }
      };
    }
  },

  // 언어 설정 변경
  updateLanguage: async (language) => {
    try {
      const userId = localStorage.getItem("user_id");
      const response = await apiClient.put('/user/v1/modify', { language: language, user_id: userId });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: {
          code: error.response?.status || 500,
          message: 'language_update_failed'
        }
      };
    }
  },

  // 로그아웃
  logout: async () => {
    try {
      await tokenService.removeToken();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 500,
          message: 'logout_failed'
        }
      };
    }
  },

  /**
   * 로그인 없이 작업자 페이지용 JWT 발급.
   * client_secret(REACT_APP_AUTH_WORKER_PAGE_KEY)과 user_id로 POST /worker-token 호출 후 토큰 저장.
   * 토큰으로 API 접속은 기존과 동일(x-user-jwt).
   * @param {string} userId - 토큰을 발급받을 사용자 ID
   */
  getWorkerToken: async (userId) => {
    const workerPageKey = process.env.REACT_APP_AUTH_WORKER_PAGE_KEY;
    const baseUrl = API_CONFIG.BASE_URL || process.env.REACT_APP_API_BASE_URL;

    if (!workerPageKey) {
      return {
        success: false,
        error: {
          code: 400,
          message: 'REACT_APP_AUTH_WORKER_PAGE_KEY가 .env에 설정되지 않았습니다. 설정 후 개발 서버를 재시작하세요.'
        }
      };
    }
    if (!baseUrl) {
      return {
        success: false,
        error: {
          code: 400,
          message: 'REACT_APP_API_BASE_URL이 .env에 설정되지 않았습니다.'
        }
      };
    }
    const userIdTrimmed = typeof userId === 'string' ? userId.trim() : '';
    if (!userIdTrimmed) {
      return {
        success: false,
        error: {
          code: 400,
          message: '사용자 ID를 입력해 주세요.'
        }
      };
    }
    try {
      const formData = new URLSearchParams();
      formData.append('client_secret', workerPageKey);
      formData.append('user_id', userIdTrimmed);

      const response = await axios.post(`${String(baseUrl).replace(/\/$/, '')}/worker-token`, formData.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        timeout: 15000
      });

      if (response.status === 200 && response.data?.access_token) {
        await tokenService.setToken(response.data.access_token);
        if (response.data.expire_time_at_utc) {
          tokenService.setTokenExpiry(response.data.expire_time_at_utc);
        }
        return {
          success: true,
          data: {
            access_token: response.data.access_token,
            expire_time_at_utc: response.data.expire_time_at_utc
          }
        };
      }

      return {
        success: false,
        error: {
          code: response.status,
          message: response.data?.detail || 'worker_token_failed'
        }
      };
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message;
      return {
        success: false,
        error: {
          code: status || 500,
          message: typeof detail === 'string' ? detail : 'worker_token_failed'
        }
      };
    }
  }
}; 